import {global} from '../../appContext'
import {MAX_RESULTS_PER_PAGE} from '../../constants'
import {TServiceName} from '../../constants/ApiServices'
import {ISearchParams} from '../../index'
import PromiseRequest from '../../req/PromiseRequest'
import {getSearchParamsFn} from '../../util/request'

export interface IComboboxApiSource {
    api: TServiceName
    isSearch?: boolean
    params?: Partial<ISearchParams>
    killCacheOnUpdate?: boolean
}

/** Abortable promise factory for a named API service (combobox list load). */
export function createServiceListPromise<T>(opts: {
    name: TServiceName
    params: ISearchParams | (() => ISearchParams)
    onSuccess?: (data: T[]) => void
}): {promise: () => Promise<T[]>, onDispose: () => void} {
    let promiseRequest: PromiseRequest | undefined
    return {
        promise: () => {
            promiseRequest?.abort()
            promiseRequest = global.getPromiseRequest(opts.name)
            const requestParams = opts.params
            return promiseRequest
                .request(typeof requestParams === 'function' ? requestParams() : requestParams)
                .then((data: T[]) => {
                    opts.onSuccess?.(data)
                    return data as T[]
                })
        },
        onDispose: () => promiseRequest?.abort(),
    }
}

/** Space-token sequential match on a haystack (legacy base `_filterOnInput` label path). */
export function matchesSpaceTokens(haystack: string, needle: string): boolean {
    let index = 0
    return needle.split(' ').every((token: string) => {
        if (!token) {
            return true
        }
        const idx = haystack.indexOf(token, index)
        if (idx === -1) {
            return false
        }
        index = idx + token.length
        return true
    })
}

/** Live text filter matching legacy base: searchableProperties OR tokenized label. */
export function createBaseTextMatch<T>(
    map2input: (o: T) => string,
    searchableProperties?: (keyof T)[],
): (item: T, query: string) => boolean {
    return (item, query) => {
        if (!query) {
            return true
        }
        if (searchableProperties?.length) {
            if (searchableProperties.some(p => String(item[p] ?? '').toLowerCase().indexOf(query) > -1)) {
                return true
            }
        }
        const haystack = map2input(item).toLowerCase()
        if (!haystack) {
            return true
        }
        return matchesSpaceTokens(haystack, query)
    }
}

/** Abortable list promise for a named API / searchobject-style source. */
export function createApiListPromise<T>(apiSource: IComboboxApiSource): {
    promise: () => Promise<T[]>
    onDispose: () => void
    /** Call instead of legacy `_doUpdate` when killCacheOnUpdate is set. */
    reloadWithOptionalCacheKill: (reload?: () => void) => void
} {
    let promiseRequest: PromiseRequest | undefined
    return {
        promise: () => {
            promiseRequest?.abort()
            promiseRequest = global.getPromiseRequest(apiSource.api)
            let requestParams: Partial<ISearchParams> = {}
            if (apiSource.isSearch) {
                requestParams = getSearchParamsFn(apiSource.api)(
                    {resultsprpage: MAX_RESULTS_PER_PAGE},
                    apiSource.params ?? {},
                )
            } else {
                requestParams = apiSource.params ?? {}
            }
            return promiseRequest.request(requestParams) as Promise<T[]>
        },
        onDispose: () => promiseRequest?.abort(),
        reloadWithOptionalCacheKill: (reload) => {
            if (apiSource.killCacheOnUpdate) {
                global.cacheManager.removeCache(apiSource.api)
            }
            reload?.()
        },
    }
}
